import type { Annotation } from '../../annotation';
import type { DamageAnnotationMetadata, InspectionDamage } from '../inspectionTypes';
import type { InspectionMediaData } from '../inspectionMediaTypes';

type DamageLikeInput = Pick<
  InspectionDamage,
  | 'id'
  | 'source'
  | 'detectedBy'
  | 'review_status'
  | 'isDeleted'
  | 'deleted'
  | 'newMetadata'
  | 'metadata'
  | 'new_metadata'
  | 'damageMetadata'
  | 'damage_metadata'
  | 'annotation'
> &
  Record<string, unknown>;

const isTruthyDeletedFlag = (value: unknown): boolean =>
  value === true || value === 'true' || value === 1 || value === '1';

/**
 * Soft-deleted damages remain in GET /damages on both platforms.
 * Cloud often clears metadata on delete; on-prem keeps it — always prefer
 * isDeleted / status over metadata presence when hiding rows from the UI.
 */
export const isDamageDeleted = (
  damage?: (Pick<InspectionDamage, 'isDeleted' | 'deleted' | 'status'> & Record<string, unknown>) | null
): boolean => {
  if (!damage) {
    return false;
  }

  if (
    isTruthyDeletedFlag(damage.isDeleted) ||
    isTruthyDeletedFlag(damage.deleted) ||
    isTruthyDeletedFlag(damage.is_deleted) ||
    isTruthyDeletedFlag(damage.IsDeleted)
  ) {
    return true;
  }

  const status = String(damage.status ?? '').toLowerCase();
  return status === 'deleted' || status === 'delete';};

export const parseDamageMetadata = (metadata: unknown): DamageAnnotationMetadata | null => {
  if (!metadata) {
    return null;
  }

  if (typeof metadata === 'string') {
    try {
      const parsed = JSON.parse(metadata);
      return parsed && typeof parsed === 'object' ? (parsed as DamageAnnotationMetadata) : null;
    } catch {
      return null;
    }
  }

  if (typeof metadata === 'object') {
    return metadata as DamageAnnotationMetadata;
  }

  return null;
};

export const getDamageMetadata = (damage?: DamageLikeInput | null): DamageAnnotationMetadata | null =>
  parseDamageMetadata(
    damage?.newMetadata ??
      damage?.metadata ??
      damage?.new_metadata ??
      damage?.damageMetadata ??
      damage?.damage_metadata ??
      damage?.annotation
  );

export const buildAnnotations = (damages: DamageLikeInput[]): Annotation[] =>
  damages
    .map(damage => {
      const meta = getDamageMetadata(damage);
      if (!meta) return null;

      const metaType = typeof meta.type === 'string' ? meta.type : '';
      const source = typeof damage.source === 'string' ? damage.source : '';

      return {
        ...meta,
        id: String(damage.id ?? meta.id ?? ''),
        x: (meta.x ?? 0) < 0.2 ? 0.1 : (meta.x ?? 0),
        x1: (meta.x1 ?? 0) < 0.2 ? 0.1 : (meta.x1 ?? 0),
        y: (meta.y ?? 0) < 0.2 ? 0.1 : (meta.y ?? 0),
        y1: (meta.y1 ?? 0) < 0.2 ? 0.1 : (meta.y1 ?? 0),
        deleted: isDamageDeleted(damage) ? 1 : 0,
        review_status:
          typeof damage.review_status === 'string'
            ? damage.review_status
            : typeof (damage as { reviewStatus?: unknown }).reviewStatus === 'string'
              ? ((damage as { reviewStatus?: string }).reviewStatus as string)
              : undefined,
        created_by: typeof damage.detectedBy === 'string' || typeof damage.detectedBy === 'number' ? damage.detectedBy : undefined,
        action: undefined,
        type: metaType && metaType.length < 7 ? `${metaType.substring(0, 5)}-${source?.[0]?.toUpperCase() || ''}` : metaType,
      } as Annotation;
    })
    .filter((annotation): annotation is Annotation => Boolean(annotation));

export const areAnnotationsEqual = (left: Annotation[], right: Annotation[]) => {
  if (left === right) return true;
  if (left.length !== right.length) return false;

  return left.every((annotation, index) => {
    const nextAnnotation = right[index];

    return (
      annotation?.id === nextAnnotation?.id &&
      annotation?.type === nextAnnotation?.type &&
      annotation?.x === nextAnnotation?.x &&
      annotation?.y === nextAnnotation?.y &&
      annotation?.x1 === nextAnnotation?.x1 &&
      annotation?.y1 === nextAnnotation?.y1 &&
      annotation?.review_status === nextAnnotation?.review_status &&
      annotation?.deleted === nextAnnotation?.deleted
    );
  });
};

export const getDamageLookupImageUri = (imageData?: Pick<InspectionMediaData, 'imageURI' | 'src'> | null): string => {
  const imageURI = imageData?.imageURI ?? imageData?.src;

  if (!imageURI) {
    return '';
  }

  if (typeof imageURI === 'string' && imageURI.startsWith('http')) {
    try {
      const parsedUrl = new URL(imageURI);
      return parsedUrl.pathname.startsWith('/') ? parsedUrl.pathname : `/${parsedUrl.pathname}`;
    } catch {
      return imageURI;
    }
  }

  const imagePath = String(imageURI)
    .replace(/^https?:\/\/[^/]+\//, '')
    .replace(/^\/+/, '');
  return imagePath ? `/${imagePath}` : '';
};

export const hasUnsavedAnnotationChanges = (annotations: Annotation[]) =>
  annotations.some(annotation => {
    // Ignore already saved/updated annotations
    if (annotation?.action === 'updated') {
      return false;
    }
    return annotation?.deleted !== 1 && (String(annotation?.id ?? '').includes('new_') || Boolean(annotation?.action));
  });
